import { useQuery } from '@tanstack/react-query';
import { schoolClassificationApi } from '@/features/reports/api';
import { ApiError } from '@/shared/api';
import { isDashboardSchoolId } from './useSchoolClassificationDashboardQuery';

export const classTuitionByProgramQueryKeys = {
  all: ['school-classification', 'class-tuition-by-program'] as const,
  detail: (schoolId: string, year: number) =>
    [...classTuitionByProgramQueryKeys.all, schoolId, year] as const,
};

/**
 * GET /api/app/school-classification/class-tuition-by-program/{schoolId}?Year=…
 * Returns tuition-per-student broken down by class program for one school + year.
 */
export function useClassTuitionByProgramQuery(
  schoolId: string | undefined,
  year: string | undefined,
  options?: { enabled?: boolean }
) {
  const y = year && /^\d+$/.test(year.trim()) ? parseInt(year.trim(), 10) : NaN;
  const baseEnabled =
    isDashboardSchoolId(schoolId) && Number.isFinite(y) && y > 0;
  const enabled = (options?.enabled ?? true) && baseEnabled;

  return useQuery({
    queryKey: classTuitionByProgramQueryKeys.detail(schoolId ?? '', y || 0),
    queryFn: () => schoolClassificationApi.getClassTuitionByProgram(schoolId!, y),
    enabled,
    retry: false,
  });
}

export function classTuitionByProgramErrorMessage(err: unknown): string {
  return err instanceof ApiError
    ? ApiError.getUserMessage(err) ?? err.message
    : 'Could not load tuition per program.';
}
